// ============================================================================
// APP - MAIN ENTRY POINT
// React Router setup with Layout
// ============================================================================

import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import type { Session } from '@supabase/supabase-js'
import { Toaster } from '@/components/ui/toaster'
import { usePostHog } from 'posthog-js/react'
import Layout from '@/components/layout'

// Lazy Load Pages
const Dashboard = lazy(() => import('@/pages/dashboard'))
const Schedule = lazy(() => import('@/pages/schedule'))
const Progress = lazy(() => import('@/pages/progress'))
const Settings = lazy(() => import('@/pages/settings'))
const IntervalsCallback = lazy(() => import('@/pages/auth/intervals-callback'))
const Onboarding = lazy(() => import('@/pages/onboarding'))
const IntervalsTest = lazy(() => import('@/pages/intervals-test'))
const PrintSchedule = lazy(() => import('@/pages/print'))
const WorkoutPlayer = lazy(() => import('@/pages/player'))
const StravaCallback = lazy(() => import('@/pages/strava-callback'))
const RaceStrategyPage = lazy(() => import('@/pages/race'))
const LandingPage = lazy(() => import('@/pages/landing'))
const FeaturesPage = lazy(() => import('@/pages/features'))
const AboutPage = lazy(() => import('@/pages/about'))
const PricingPage = lazy(() => import('@/pages/pricing'))
const RoadmapPage = lazy(() => import('@/pages/roadmap'))
const LoginPage = lazy(() => import('@/pages/login'))
const AcceptInvite = lazy(() => import('@/pages/onboarding/AcceptInvite'))
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'))
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage'))
const CookiePolicyPage = lazy(() => import('@/pages/legal/CookiePolicyPage'))
const FeaturesAdminPage = lazy(() => import('@/pages/admin/features'))
const InviteSuccess = lazy(() => import('@/pages/onboarding/InviteSuccess'))
const AthleteWelcome = lazy(() => import('@/pages/onboarding/AthleteWelcome'))
const LogbookPage = lazy(() => import('@/pages/logbook'))
const DebugHealthPage = lazy(() => import('@/pages/debug/health'))

import { Progress as ProgressBar } from '@/components/ui/progress'
import { Zap } from 'lucide-react'
import { CookieConsent } from '@/components/legal/CookieConsent'
import { PublicLayout } from '@/components/layout/PublicLayout'
import { NotFound } from '@/pages/error/ErrorPages'
import { RouteSeo } from '@/components/seo/RouteSeo'

import { Walkthrough } from '@/components/onboarding/Walkthrough'
import { OnboardingPlanDialog } from '@/components/features/onboarding/OnboardingPlanDialog'
import { DataOverwriteDialog } from '@/components/features/sync/DataOverwriteDialog'
import { DataMergeDialog } from '@/components/features/sync/DataMergeDialog'
import { AuthGate } from '@/components/auth/AuthGate'
import { initAutoSync, syncPublicFeatureFlags } from './services/cloudSync';
import { ImpersonationBanner } from '@/components/ui/ImpersonationBanner';
import { useSessionBootstrap } from '@/hooks/useSessionBootstrap'
import { isOnboardingSuppressed } from '@/lib/first-run-preferences'
import { FEATURE_PROGRESS_PAGE } from '@/types'
import './index.css'

const LOADING_MESSAGES = [
  'Rest weeks are not weakness. Joop Zoetemelk said it best: you win the Tour in bed.',
  'We increase volume slowly so your tendons keep up with your lungs. Burnout is not heroic.',
  'Tapering removes fatigue so fitness can finally show up. Many races are lost by overtraining late.',
  'Keep easy days embarrassingly easy so hard days can be properly hard. Eliud Kipchoge calls this discipline.',
  'Consistency beats hero workouts. Injured athletes have great excuses and zero fitness.',
  'Zone 2 feels too slow on purpose. Tadej Pogacar builds his engine on endless aerobic hours.',
  'Base training still includes intensity. Mathieu van der Poel never fully drops high-intensity work.',
  'Long steady efforts build mitochondria. This is how endurance athletes earn durability.',
  'High-intensity intervals raise VO2max and lactate threshold. Sifan Hassan uses them year-round.',
  'Strength training improves economy and injury resistance. Jonas Abrahamsen lifts. So should you.',
  'Warm-ups and cool-downs are mandatory. Skipping them borrows time from your future body.',
  'Neuromuscular strides teach speed without strain. Faith Kipyegon uses them to stay fast and relaxed.',
  'Most adaptation happens during sleep. Skip sleep and you are just exercising.',
  'Soreness is normal. Sharp pain is your body shouting. Learn the language early.',
  'Cross-training keeps fitness high while sparing joints. Paula Radcliffe relied on it to survive mileage.',
  'Fuel the work. Eat carbs 2 to 3 hours before hard sessions or expect disappointment.',
  'Hydration starts yesterday. Consistency matters more than mid-workout panic drinking.',
  'Protein within 30 minutes after strength work accelerates repair. Muscles are impatient.',
  'Feeling empty or heavy usually means you under-fueled yesterday, not today.',
  'Never try new nutrition on race day. Champions rehearse fueling like choreography.',
  'Mental stress counts as training load. Training plans ignore life at their own risk.',
  'Mobility is maintenance, not training. Ten minutes while watching TV is enough to stay functional.',
  'Alcohol hurts sleep and recovery. You can drink or adapt faster. Pick one occasionally.',
  'A 20-minute nap boosts alertness and lowers perceived effort. Many champions protect naps fiercely.',
  'Recovery is where fitness appears. The workout just opens the door.',
] as const;

const LOADING_MESSAGE_STORAGE_KEY = 'ae_last_loading_message_index';
const LOADING_MESSAGE_ROTATION_MS = 20000;

function pickDifferentMessageIndex(previousIndex: number | null): number {
  if (LOADING_MESSAGES.length <= 1) return 0;

  let nextIndex = Math.floor(Math.random() * LOADING_MESSAGES.length);
  while (nextIndex === previousIndex) {
    nextIndex = Math.floor(Math.random() * LOADING_MESSAGES.length);
  }
  return nextIndex;
}

function getInitialLoadingMessageIndex(): number {
  if (typeof window === 'undefined') return 0;

  const rawValue = window.localStorage.getItem(LOADING_MESSAGE_STORAGE_KEY);
  const parsed = rawValue !== null ? Number.parseInt(rawValue, 10) : Number.NaN;
  const previousIndex =
    Number.isInteger(parsed) && parsed >= 0 && parsed < LOADING_MESSAGES.length
      ? parsed
      : null;

  const nextIndex = pickDifferentMessageIndex(previousIndex);
  window.localStorage.setItem(LOADING_MESSAGE_STORAGE_KEY, String(nextIndex));
  return nextIndex;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const athlete = useStore(state => state.athlete)
  const authUser = useStore(state => state.authUser)
  const isHydrating = useStore(state => state.isHydrating)
  const [hasHydrated, setHasHydrated] = useState(() => useStore.persist.hasHydrated())

  useEffect(() => {
    if (hasHydrated) return

    const unsubscribe = useStore.persist.onFinishHydration(() => {
      setHasHydrated(true)
    })

    return unsubscribe
  }, [hasHydrated])

  if (!hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="animate-pulse text-slate-400 font-medium">Loading your plan...</div>
      </div>
    );
  }

  if (!authUser?.id) {
    return <Navigate to="/login" replace />
  }

  if (!athlete.name) {
    // While cloud hydration is in flight (profile switch, impersonation,
    // fresh-device login) an empty athlete means "not loaded yet", not
    // "not onboarded". Redirecting now would bounce users to /onboarding.
    if (isHydrating) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
          <div className="animate-pulse text-slate-400 font-medium">Loading your plan...</div>
        </div>
      );
    }
    if (isOnboardingSuppressed()) {
      return <>{children}</>
    }
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

// Specialized wrapper for Print
function PrintRoute({ children }: { children: React.ReactNode }) {
  const athlete = useStore(state => state.athlete)
  const authUser = useStore(state => state.authUser)
  const plan = useStore(state => state.plan)
  const isHydrating = useStore(state => state.isHydrating)
  const [hasHydrated, setHasHydrated] = useState(() => useStore.persist.hasHydrated())

  useEffect(() => {
    if (hasHydrated) return

    const unsubscribe = useStore.persist.onFinishHydration(() => {
      setHasHydrated(true)
    })

    return unsubscribe
  }, [hasHydrated])

  if (!hasHydrated) {
    return <div className="p-8 text-center text-slate-400">Preparing printer view...</div>
  }

  if (!authUser?.id) {
    return <Navigate to="/login" replace />
  }

  if (!athlete.name && plan.length === 0) {
    if (isHydrating) {
      return <div className="p-8 text-center text-slate-400">Preparing printer view...</div>
    }
    if (isOnboardingSuppressed()) {
      return <>{children}</>
    }
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

// Auth-only route wrapper
function AuthProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles?: string[];
}) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const userRole = useStore(state => state.userRole)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="animate-pulse text-slate-400 font-medium">Verifying session...</div>
      </div>
    );
  }

  if (!session) {
    return <AuthGate />
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Feature flag protected route wrapper
function FeatureProtectedRoute({
  children,
  featureKey,
}: {
  children: React.ReactNode;
  featureKey: string;
}) {
  const checkFeatureAccess = useStore(state => state.checkFeatureAccess);

  if (!checkFeatureAccess(featureKey)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirects to dashboard if logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const authUser = useStore(state => state.authUser)
  const [hasHydrated, setHasHydrated] = useState(() => useStore.persist.hasHydrated())

  useEffect(() => {
    if (hasHydrated) return

    const unsubscribe = useStore.persist.onFinishHydration(() => {
      setHasHydrated(true)
    })

    return unsubscribe
  }, [hasHydrated])

  // Until persist hydration finishes we cannot trust authUser. Rendering the
  // landing page in the meantime keeps the homepage visible (vs a blank flash)
  // and avoids redirecting based on a transient store snapshot — which is what
  // produced the "/" -> "/dashboard" -> "/onboarding" flicker before.
  if (!hasHydrated) {
    return <>{children}</>
  }

  if (authUser) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}



// PostHog Identification Tracker
function PostHogTracker() {
  const posthog = usePostHog()
  const userRole = useStore(state => state.userRole);
  const activeProfileId = useStore(state => state.activeProfileId);
  const athlete = useStore(state => state.athlete);
  const analyticsConsent = useStore(state => state.consent.analytics)

  useEffect(() => {
    if (activeProfileId && posthog && analyticsConsent) {
      posthog.identify(activeProfileId, {
        name: athlete.name,
        sport: athlete.sport,
        role: userRole
      });
    }
  }, [activeProfileId, posthog, athlete.name, athlete.sport, userRole, analyticsConsent])

  return null
}

import { useIntervalsAutoSync } from './hooks/useIntervalsAutoSync'

function AppContent() {
  const loading = useSessionBootstrap()
  const location = useLocation()
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(
    () => getInitialLoadingMessageIndex()
  )
  const [loadingProgress, setLoadingProgress] = useState(0)

  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setLoadingProgress(p => (p >= 90 ? 90 : p + Math.random() * 15));
    }, 200);
    return () => clearInterval(interval);
  }, [loading]);

  useIntervalsAutoSync(); // Global Auto-Sync

  // OPTIMIZED SELECTORS
  const incrementScreensVisited = useStore(state => state.incrementScreensVisited)
  const consent = useStore(state => state.consent)
  const activeProfileId = useStore(state => state.activeProfileId)

  // We use getState() in the effect to avoid subscription-based loop
  const posthog = usePostHog()

  // FORCE HYDRATION ON PROFILE SWITCH
  // Use a ref to track the last synced profile to prevent infinite loops
  // caused by store updates triggering the useEffect again.
  const lastSyncedProfileRef = useRef<string | null>(null);
  const isSyncingRef = useRef(false);

  useEffect(() => {
    if (!activeProfileId) return;

    // If we already synced this profile in this mounting cycle, skip
    // This assumes activeProfileId doesn't flip-flop rapidly without unmounts.
    // If it does, we might need a timestamp check, but this is a good start.
    if (lastSyncedProfileRef.current === activeProfileId) return;

    const currentProfile = useStore.getState().profiles.find(p => p.id === activeProfileId);

    if (currentProfile && currentProfile.relation === 'coached') {
      if (isSyncingRef.current) return;

      console.log('[App] Switched to Coached Profile. Forcing Cloud Hydration...');
      isSyncingRef.current = true;
      lastSyncedProfileRef.current = activeProfileId;

      // We import dynamically to avoid circular deps if any, though likely fine here.
      import('./services/cloudSync').then(({ downloadCloudDataToLocal }) => {
        downloadCloudDataToLocal(activeProfileId, false).finally(() => {
          isSyncingRef.current = false;
        });
      }).catch(err => {
        console.error('[App] Failed to import/run cloud hydration:', err);
        isSyncingRef.current = false;
      });
    }
  }, [activeProfileId]); // REMOVED profiles from deps to prevent loop

  useEffect(() => {
    incrementScreensVisited()
    // Track page views in PostHog only if analytics consent is given
    if (posthog && consent.analytics) {
      posthog.capture('$pageview')
    }
  }, [location.pathname, incrementScreensVisited, posthog, consent.analytics])

  useEffect(() => {
    const unsubscribe = initAutoSync();
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    void syncPublicFeatureFlags().catch((error) => {
      console.error('[App] Failed to load public feature flags:', error);
    });
  }, []);

  useEffect(() => {
    if (!loading) return;

    const intervalId = window.setInterval(() => {
      setLoadingMessageIndex((currentIndex) => {
        const nextIndex = pickDifferentMessageIndex(currentIndex);
        window.localStorage.setItem(LOADING_MESSAGE_STORAGE_KEY, String(nextIndex));
        return nextIndex;
      });
    }, LOADING_MESSAGE_ROTATION_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loading]);

  const loadingMessage = LOADING_MESSAGES[loadingMessageIndex] ?? LOADING_MESSAGES[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="flex flex-col items-center gap-6 w-full max-w-sm px-6">
          <div className="flex items-center gap-3 mb-2">
            <Zap className="w-8 h-8 text-primary animate-pulse" />
            <span className="font-bold text-xl tracking-tight text-slate-800 dark:text-slate-200">Adaptive Endurance</span>
          </div>
          <div className="w-full space-y-2">
            <ProgressBar value={loadingProgress} className="h-1.5" />
            <div className="flex justify-between items-center text-xs font-medium text-slate-400">
              <span className="animate-pulse">Waking up the engine...</span>
              <span>{Math.round(loadingProgress)}%</span>
            </div>
          </div>
          <p className="mt-8 text-center text-sm font-medium text-slate-500 italic max-w-xs">
            "{loadingMessage}"
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PostHogTracker />
      <RouteSeo />
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      }>
        <Routes>

          {/* Public Pages wrapped in PublicLayout */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          </Route>

          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/invite" element={<AcceptInvite />} />
          <Route path="/onboarding/invite-success" element={<InviteSuccess />} />
          <Route path="/onboarding/athlete-welcome" element={<AthleteWelcome />} />

          {/* Onboarding */}
          <Route path="/onboarding" element={<Onboarding />} />

          {/* Callbacks */}
          <Route path="/strava/callback" element={<StravaCallback />} />
          <Route path="/strava-callback" element={<StravaCallback />} />
          <Route path="/auth/callback/intervals" element={<IntervalsCallback />} />

          {/* Protected Application Routes */}
          <Route element={
            <>
              <ImpersonationBanner />
              <Layout />
            </>
          }>
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
            <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
            <Route
              path="/progress"
              element={
                <ProtectedRoute>
                  <FeatureProtectedRoute featureKey={FEATURE_PROGRESS_PAGE}>
                    <Progress />
                  </FeatureProtectedRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/race" element={<ProtectedRoute><RaceStrategyPage /></ProtectedRoute>} />
            <Route path="/race" element={<ProtectedRoute><RaceStrategyPage /></ProtectedRoute>} />
            <Route path="/intervals-test" element={<ProtectedRoute><IntervalsTest /></ProtectedRoute>} />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/logbook"
              element={
                <ProtectedRoute>
                  <LogbookPage />
                </ProtectedRoute>
              }
            />


            <Route path="/player" element={<ProtectedRoute><WorkoutPlayer /></ProtectedRoute>} />
            <Route
              path="/admin/features"
              element={
                <AuthProtectedRoute allowedRoles={['admin', 'superadmin']}>
                  <FeaturesAdminPage />
                </AuthProtectedRoute>
              }
            />
            <Route
              path="/debug/health"
              element={
                <AuthProtectedRoute allowedRoles={['admin', 'superadmin']}>
                  <DebugHealthPage />
                </AuthProtectedRoute>
              }
            />
          </Route>

          {/* Global Access Routes */}
          <Route
            path="/print"
            element={
              <PrintRoute>
                <PrintSchedule />
              </PrintRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Toaster />
      <Walkthrough />
      <OnboardingPlanDialog />
      <DataOverwriteDialog />
      <DataMergeDialog />
      <CookieConsent />
    </>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  )
}

export default App
